import React, { useState, useEffect } from 'react';
import { Menu, X, Home, Code, FolderGit2, Briefcase, Info, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setHasScrolled(scrollPosition > 0);

      // Get total scroll height
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      // If we're at the bottom of the page, set contact as active
      if (Math.ceil(window.scrollY) >= totalHeight - 10) {
        setActiveSection('contact');
        return;
      }

      // Check which section is currently in view
      const sections = navItems.map(item => item.name.toLowerCase());
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 100;
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock/unlock body scroll when mobile menu is opened/closed
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navItems = [
    { id: 0, name: 'Home', Icon: Home },
    { id: 1, name: 'Skills', Icon: Code },
    { id: 2, name: 'Projects', Icon: FolderGit2 },
    { id: 3, name: 'Experience', Icon: Briefcase },
    { id: 4, name: 'More Info', Icon: Info },
    { id: 5, name: 'Contact', Icon: Mail }
  ];

  const handleNavClick = (e, itemName) => {
    e.preventDefault();
    const targetId = itemName.toLowerCase();
    const element = document.getElementById(targetId);
    
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });

      setActiveSection(targetId);
      setIsOpen(false);
    }
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    closed: { x: -16, opacity: 0 },
    open: i => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.2,
        ease: "easeOut"
      }
    })
  };

  const navbarClasses = `fixed font-lato w-full top-0 z-50 p-4 transition-all duration-300 ${
    hasScrolled 
      ? 'bg-[#0d0d0d]/30 backdrop-blur-md backdrop-saturate-150' 
      : 'bg-[#0d0d0d]/30 backdrop-blur-md backdrop-saturate-150'
  }`;

  const getItemClass = (itemName, isMobile = false) => {
    const isActive = activeSection === itemName.toLowerCase();
    return `relative ${
      isActive ? 'text-white' : 'text-gray-300'
    } hover:text-white transition-colors duration-200 flex items-center gap-2 group ${
      isMobile ? 'w-full px-4' : ''
    }`;
  };

  return (
    <nav className={navbarClasses}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <span className="text-white text-2xl font-extrabold">alroy<span className='text-[#2fe22f]'>.</span>p</span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.a
                key={item.id}
                href={`#${item.name.toLowerCase()}`}
                className={getItemClass(item.name)}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: item.id * 0.1 }}
                whileHover={{ y: -2 }}
                onClick={(e) => handleNavClick(e, item.name)}
              >
                <item.Icon className="w-4 h-4 text-[#2fe22f]" />
                <span>{item.name}</span>
                {/* Sliding underline */}
                <motion.div
                  className="absolute -bottom-1 left-0 h-0.5 bg-[#2fe22f] w-full origin-left"
                  initial={{ scaleX: 0 }}
                  animate={{ 
                    scaleX: activeSection === item.name.toLowerCase() ? 1 : 0 
                  }}
                  transition={{ duration: 0.3 }}
                />
                {/* Hover underline */}
                <motion.div
                  className="absolute -bottom-1 left-0 h-0.5 bg-[#2fe22f] w-full origin-left"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.a>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-white focus:outline-none"
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className=""
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
            >
              <motion.div 
                className="flex flex-col py-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {navItems.map((item) => (
                  <motion.a
                    key={item.id}
                    custom={item.id}
                    variants={itemVariants}
                    href={`#${item.name.toLowerCase()}`}
                    className={getItemClass(item.name, true)}
                    onClick={(e) => handleNavClick(e, item.name)}
                    whileHover={{ x: 4 }}
                  >
                    <div className="flex items-center py-3 w-full">
                      <item.Icon className="w-4 h-4 text-[#2fe22f]" />
                      <span className="ml-2">{item.name}</span>
                      {/* Mobile sliding underline - thinner and contained */}
                      {activeSection === item.name.toLowerCase() && (
                        <motion.div
                          className="absolute bottom-0 left-4 right-4 h-[1px] bg-[#2fe22f]"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </div>
                  </motion.a>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;